import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _isEqual from "lodash/isEqual";
import _debounce from "lodash/debounce";
import "core-js/modules/es6.function.name.js";
// Copyright (c) 2016, Team Magneto
//
// Permission to use, copy, modify, and/or distribute this software for any
// purpose with or without fee is hereby granted, provided that the above
// copyright notice and this permission notice appear in all copies.
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
// WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
// MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
// ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
// WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
// ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
// OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
//
// Source from https://github.com/team-767/react-within-viewport
// with minor syntax tweaks to play nicely with ES2015
// and React 15.
// Higher order component that supplies a boolean prop `inViewport` to
// wrapped components. This prop corresponds to whether an element is inside
// the bounds of the screen (for instance, something below the fold would get
// false as the value for this prop).
//
// This component checks boundingClientRects to get the bounding box, as well
// as clientRects().length to detect display status. This is done on
// (throttled) resize and scroll listeners.
import React, { Component } from 'react';
import PropTypes from 'prop-types';

var defaultGetHeight = function defaultGetHeight() {
  var elem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (elem && elem.getBoundingClientRect) {
    return elem.getBoundingClientRect().height;
  }

  if (elem) {
    return elem.clientHeight;
  }

  return null;
};

var defaultGetWidth = function defaultGetWidth() {
  var elem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (elem && elem.getBoundingClientRect) {
    return elem.getBoundingClientRect().width;
  }

  if (elem) {
    return elem.clientWidth;
  }

  return null;
};

var defaultGetTop = function defaultGetTop() {
  var elem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (elem && elem.getBoundingClientRect) {
    return elem.getBoundingClientRect().top;
  }

  if (elem) {
    return elem.clientTop;
  }

  return null;
};

var defaultGetLeft = function defaultGetLeft() {
  var elem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (elem && elem.getBoundingClientRect) {
    return elem.getBoundingClientRect().left;
  }

  if (elem) {
    return elem.clientLeft;
  }

  return null;
};

var defaultGetViewportHeight = function defaultGetViewportHeight() {
  var _window = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _document = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (_window || _document) {
    return _window.innerHeight || _document.clientHeight;
  }

  return null;
};

var defaultGetViewportWidth = function defaultGetViewportWidth() {
  var _window = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _document = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (_window || _document) {
    return _window.innerWidth || _document.clientWidth;
  }

  return null;
};
/**
 * Returns clientRects for the given element.
 *
 * @function
 * @param {Element} elem - the element to get client rects for.
 * @returns {DOMRect[]} - A collection of DOMRect objects. [] for falsy elem.
 */


var defaultGetClientRects = function defaultGetClientRects(elem) {
  return elem && elem.getClientRects() || [];
};

var defaultTransform = function defaultTransform(_ref) {
  var inViewport = _ref.inViewport;
  return {
    inViewport: inViewport
  };
};

var withinViewport = function withinViewport() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$transform = _ref2.transform,
      transform = _ref2$transform === void 0 ? defaultTransform : _ref2$transform,
      _ref2$getHeight = _ref2.getHeight,
      getHeight = _ref2$getHeight === void 0 ? defaultGetHeight : _ref2$getHeight,
      _ref2$getWidth = _ref2.getWidth,
      getWidth = _ref2$getWidth === void 0 ? defaultGetWidth : _ref2$getWidth,
      _ref2$getTop = _ref2.getTop,
      getTop = _ref2$getTop === void 0 ? defaultGetTop : _ref2$getTop,
      _ref2$getLeft = _ref2.getLeft,
      getLeft = _ref2$getLeft === void 0 ? defaultGetLeft : _ref2$getLeft,
      _ref2$getClientRects = _ref2.getClientRects,
      getClientRects = _ref2$getClientRects === void 0 ? defaultGetClientRects : _ref2$getClientRects,
      _ref2$getViewportHeig = _ref2.getViewportHeight,
      getViewportHeight = _ref2$getViewportHeig === void 0 ? defaultGetViewportHeight : _ref2$getViewportHeig,
      _ref2$getViewportWidt = _ref2.getViewportWidth,
      getViewportWidth = _ref2$getViewportWidt === void 0 ? defaultGetViewportWidth : _ref2$getViewportWidt,
      _ref2$defaultAnswer = _ref2.defaultAnswer,
      defaultAnswer = _ref2$defaultAnswer === void 0 ? false : _ref2$defaultAnswer,
      _ref2$wait = _ref2.wait,
      wait = _ref2$wait === void 0 ? 200 : _ref2$wait,
      _ref2$onViewportEnter = _ref2.onViewportEnter,
      onViewportEnter = _ref2$onViewportEnter === void 0 ? null : _ref2$onViewportEnter,
      _ref2$onViewportLeave = _ref2.onViewportLeave,
      onViewportLeave = _ref2$onViewportLeave === void 0 ? null : _ref2$onViewportLeave;

  return function (BaseComponent) {
    var Decorator = /*#__PURE__*/function (_Component) {
      _inherits(Decorator, _Component);

      var _super = _createSuper(Decorator);

      function Decorator(props) {
        var _this;

        _classCallCheck(this, Decorator);

        _this = _super.call(this, props);
        _this.state = {
          containerWidth: null,
          containerHeight: null,
          containerTopOffset: null,
          containerLeftOffset: null,
          numberOfClientRects: null,
          windowWidth: null,
          windowHeight: null,
          ready: false
        }; // We construct our ref func here so React can do an equality check
        // on the func ref and only call it once. Using an anonymous fn will result
        // in `null` being set on the second render as our debounce handler can update
        // state outside of a regular render lifecycle
        // - https://github.com/facebook/react/issues/4533
        // - https://github.com/facebook/react/issues/6249

        _this.assignViewportContainer = function (el) {
          _this.withinViewportContainer = el;
        };

        return _this;
      }

      _createClass(Decorator, [{
        key: "componentDidMount",
        value: function componentDidMount() {
          if (typeof window !== 'undefined' && window !== null && window.addEventListener) {
            this.canUpdate = true;
            this.debouncedUpdateResize = _debounce(this.updateResize.bind(this), wait);
            this.debouncedUpdateScroll = _debounce(this.updateScroll.bind(this), wait);
            window.addEventListener('resize', this.debouncedUpdateResize, {
              passive: true
            });
            var options = {
              passive: true,
              capture: true
            };
            window.addEventListener('scroll', this.debouncedUpdateScroll, options);
            this.updateResize();
            this.updateScroll();
          }
        }
      }, {
        key: "shouldComponentUpdate",
        value: function shouldComponentUpdate(nextProps, nextState) {
          return !_isEqual(this.props, nextProps) || !_isEqual(transform(_objectSpread({
            inViewport: this.inViewport(this.state)
          }, this.state)), transform(_objectSpread({
            inViewport: this.inViewport(nextState)
          }, nextState)));
        }
      }, {
        key: "componentDidUpdate",
        value: function componentDidUpdate(prevProps, prevState) {
          if (onViewportEnter || onViewportLeave) {
            var was = this.inViewport(prevState);
            var is = this.inViewport(this.state);

            if (onViewportLeave) {
              if (prevState.ready === null && is === false) {
                onViewportLeave();
              }

              if (was === true && is === false) {
                onViewportLeave();
              }
            }

            if (onViewportEnter) {
              if (prevState.ready === null && is === true) {
                onViewportEnter();
              }

              if (was === false && is === true) {
                onViewportEnter();
              }
            }
          } // eslint-disable-next-line react/destructuring-assignment -- directive added automatically by Shepherd migration


          if (!prevProps.hasBeenTracked && this.props.hasBeenTracked) {
            this.stopTracking();
          }
        }
      }, {
        key: "componentWillUnmount",
        value: function componentWillUnmount() {
          this.stopTracking();
        }
      }, {
        key: "stopTracking",
        value: function stopTracking() {
          if (this.canUpdate) {
            if (typeof window !== 'undefined' && window !== null && window.removeEventListener) {
              this.canUpdate = false;
              window.removeEventListener('resize', this.debouncedUpdateResize);
              window.removeEventListener('scroll', this.debouncedUpdateScroll);
            }
          }
        }
      }, {
        key: "updateResize",
        value: function updateResize() {
          var _this2 = this;

          var update = function update() {
            if (!_this2.canUpdate) {
              return;
            }

            var elem = _this2.withinViewportContainer;
            var newState = {
              containerWidth: getWidth(elem),
              containerHeight: getHeight(elem),
              numberOfClientRects: getClientRects(elem).length,
              windowWidth: getViewportWidth(window, document),
              windowHeight: getViewportHeight(window, document)
            };

            _this2.setState(_objectSpread(_objectSpread({}, newState), {}, {
              // eslint-disable-next-line react/no-access-state-in-setstate -- directive added automatically by Shepherd migration
              ready: _this2.isReady(_objectSpread(_objectSpread({}, _this2.state), newState))
            }));
          };

          if (typeof window !== 'undefined' && window !== null && window.requestAnimationFrame) {
            window.requestAnimationFrame(function () {
              return update();
            });
          } else {
            update();
          }
        }
      }, {
        key: "updateScroll",
        value: function updateScroll() {
          var _this3 = this;

          var update = function update() {
            if (!_this3.canUpdate) {
              return;
            }

            var elem = _this3.withinViewportContainer;
            var newState = {
              numberOfClientRects: getClientRects(elem).length,
              containerTopOffset: getTop(elem),
              containerLeftOffset: getLeft(elem)
            };
            newState.ready = _this3.isReady(_objectSpread(_objectSpread({}, _this3.state), newState));

            _this3.setState(newState);
          };

          if (typeof window !== 'undefined' && window !== null && window.requestAnimationFrame) {
            window.requestAnimationFrame(function () {
              return update();
            });
          } else {
            update();
          }
        }
      }, {
        key: "isReady",
        value: function isReady() {
          var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.state;
          return state.numberOfClientRects !== null && state.containerWidth !== null && state.containerHeight !== null && state.containerTopOffset !== null && state.containerLeftOffset !== null && state.windowHeight !== null && state.windowWidth !== null;
        }
      }, {
        key: "inViewport",
        value: function inViewport() {
          var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.state;

          if (state.ready) {
            if (state.numberOfClientRects === 0) {
              // not having _any_ client rects indicates the element is hidden,
              // either directly or through an ancestor. See
              // https://nerdwallet.slack.com/archives/C03QTMFDS/p1533269419000064?thread_ts=1533244896.000383&cid=C03QTMFDS
              return false;
            }

            return (state.containerTopOffset < 0 && state.containerHeight + state.containerTopOffset > 0 || state.containerTopOffset >= 0 && state.containerTopOffset < state.windowHeight) && (state.containerLeftOffset < 0 && state.containerWidth + state.containerLeftOffset > 0 || state.containerLeftOffset >= 0 && state.containerLeftOffset < state.windowWidth);
          }

          return defaultAnswer;
        }
      }, {
        key: "render",
        value: function render() {
          return /*#__PURE__*/React.createElement("div", {
            ref: this.assignViewportContainer
          }, /*#__PURE__*/React.createElement(BaseComponent, _extends({}, transform(_objectSpread({
            inViewport: this.inViewport(this.state)
          }, this.state)), this.props)));
        }
      }]);

      return Decorator;
    }(Component);

    function getDisplayName(WrappedComponent) {
      return WrappedComponent.displayName || WrappedComponent.name || 'Component';
    } // eslint-disable-next-line react/static-property-placement


    Decorator.displayName = "withinViewport(".concat(getDisplayName(BaseComponent), ")"); // eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration

    Decorator.propTypes = {
      // eslint-disable-next-line react/require-default-props -- directive added automatically by Shepherd migration
      hasBeenTracked: PropTypes.bool
    };
    return Decorator;
  };
};

export default withinViewport;